import { useRef, useState } from "react";
import { Link } from "@tanstack/react-router";
import {
  Lock,
  ArrowRight,
  X,
  ChevronDown,
  ChevronRight,
  LifeBuoy,
  Sparkles,
  Flame,
  Footprints,
  Snowflake,
  Users,
  Moon,
  type LucideIcon,
} from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { PRACTICE_LIBRARY, practiceLinkProps } from "@/data/article-practice";
import {
  TRAUMA_RESPONSES,
  TRAUMA_RESPONSE_DISCLAIMER,
  TRAUMA_RESPONSE_NOT_FIXED,
  type TraumaResponse,
} from "@/data/trauma-responses";
import { ink, tint } from "@/data/feelings-pathway";
import { useMembership } from "@/hooks/useMembership";

/**
 * Trauma Response Wheel.
 *
 * Clean, wheel-first layout: a short title and intro, then a large tappable
 * coloured ring with an icon per response. Tapping a response highlights that
 * section and opens an information card directly underneath. The check-in and
 * crisis-support links sit quietly below the wheel.
 *
 * Self-reflection only — never framed as a diagnosis.
 */

const SIZE = 440;
const C = SIZE / 2;
const R_IN = 96;
const R_OUT = 206;

const RESPONSE_ICONS: Record<string, LucideIcon> = {
  fight: Flame,
  flight: Footprints,
  freeze: Snowflake,
  fawn: Users,
  shutdown: Moon,
};

function polar(r: number, deg: number) {
  const a = ((deg - 90) * Math.PI) / 180;
  return {
    x: Number((C + r * Math.cos(a)).toFixed(3)),
    y: Number((C + r * Math.sin(a)).toFixed(3)),
  };
}

function arcPath(from: number, to: number) {
  const p1 = polar(R_OUT, from);
  const p2 = polar(R_OUT, to);
  const p3 = polar(R_IN, to);
  const p4 = polar(R_IN, from);
  const large = to - from > 180 ? 1 : 0;
  return [
    `M ${p1.x} ${p1.y}`,
    `A ${R_OUT} ${R_OUT} 0 ${large} 1 ${p2.x} ${p2.y}`,
    `L ${p3.x} ${p3.y}`,
    `A ${R_IN} ${R_IN} 0 ${large} 0 ${p4.x} ${p4.y}`,
    "Z",
  ].join(" ");
}

function Wheel({
  selectedKey,
  onSelect,
}: {
  selectedKey: string | null;
  onSelect: (r: TraumaResponse) => void;
}) {
  const step = 360 / TRAUMA_RESPONSES.length;
  const selected = TRAUMA_RESPONSES.find((r) => r.key === selectedKey) ?? null;

  return (
    <div className="relative mx-auto w-full">
      <svg
        viewBox={`0 0 ${SIZE} ${SIZE}`}
        className="h-auto w-full select-none"
        role="group"
        aria-label="Trauma response wheel"
      >
        {TRAUMA_RESPONSES.map((r, i) => {
          const from = i * step + 1.4;
          const to = (i + 1) * step - 1.4;
          const mid = (from + to) / 2;
          const isSelected = r.key === selectedKey;
          const labelPos = polar((R_IN + R_OUT) / 2, mid);
          const Icon = RESPONSE_ICONS[r.key];
          return (
            <g key={r.key}>
              <path
                d={arcPath(from, to)}
                fill={tint(r.color, isSelected ? 80 : 55)}
                stroke={isSelected ? ink(r.color) : "transparent"}
                strokeWidth={isSelected ? 3 : 0}
                role="button"
                tabIndex={0}
                aria-pressed={isSelected}
                aria-label={`${r.label} — ${r.voice}`}
                className="cursor-pointer outline-none transition-all duration-200 hover:opacity-90 focus-visible:opacity-90"
                style={
                  isSelected
                    ? { filter: `drop-shadow(0 6px 14px ${tint(r.color, 45)})` }
                    : undefined
                }
                onClick={() => onSelect(r)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    onSelect(r);
                  }
                }}
              />
              {Icon ? (
                <Icon
                  x={labelPos.x - 12}
                  y={labelPos.y - 30}
                  width={24}
                  height={24}
                  color={ink(r.color)}
                  strokeWidth={1.9}
                  aria-hidden
                  pointerEvents="none"
                />
              ) : null}
              <text
                x={labelPos.x}
                y={labelPos.y + 12}
                textAnchor="middle"
                dominantBaseline="central"
                fontSize={17}
                fontWeight={700}
                fill={ink(r.color)}
                pointerEvents="none"
                style={{ fontFamily: "var(--font-emotion-label)" }}
              >
                {r.label}
              </text>
            </g>
          );
        })}
      </svg>

      <div className="pointer-events-none absolute inset-0">
        <div
          className="absolute start-1/2 top-1/2 flex -translate-x-1/2 -translate-y-1/2 flex-col items-center justify-center rounded-full border border-gold/55 bg-card/95 p-4 text-center shadow-[0_8px_24px_-8px_color-mix(in_oklab,var(--gold-deep)_45%,transparent)] ring-4 ring-card/55"
          style={{
            width: `${((R_IN * 2 - 12) / SIZE) * 100}%`,
            height: `${((R_IN * 2 - 12) / SIZE) * 100}%`,
          }}
        >
          {selected ? (
            <>
              <span
                className="font-display text-sm sm:text-base"
                style={{ color: ink(selected.color) }}
              >
                {selected.title}
              </span>
              <span className="mt-1 text-[10px] leading-relaxed text-muted-foreground sm:text-[11px]">
                “{selected.voice}”
              </span>
            </>
          ) : (
            <>
              <span className="text-xs font-semibold text-foreground [font-family:var(--font-emotion-heading)] sm:text-sm">
                How is your nervous system responding?
              </span>
              <span className="mt-1 text-[8px] font-semibold uppercase tracking-wide text-[var(--gold-deep)]">
                Tap a colour
              </span>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function SignChips({ response, previewCount }: { response: TraumaResponse; previewCount?: number }) {
  const shown = previewCount ? response.signs.slice(0, previewCount) : response.signs;
  const hidden = response.signs.length - shown.length;
  return (
    <div className="-mx-1 flex snap-x snap-mandatory gap-2 overflow-x-auto px-1 pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden sm:flex-wrap sm:overflow-visible">
      {shown.map((sign) => (
        <span
          key={sign}
          className="snap-start whitespace-nowrap rounded-full border px-3 py-1.5 text-xs sm:whitespace-normal"
          style={{
            borderColor: tint(response.color, 70),
            backgroundColor: tint(response.color, 22),
            color: ink(response.color),
          }}
        >
          {sign}
        </span>
      ))}
      {hidden > 0 && (
        <span className="snap-start inline-flex items-center gap-1 whitespace-nowrap rounded-full border border-gold/40 bg-card px-3 py-1.5 text-xs font-medium text-[var(--gold-deep)]">
          <Lock aria-hidden className="h-3 w-3" /> +{hidden} more with membership
        </span>
      )}
    </div>
  );
}

function Bullets({ items }: { items: readonly string[] }) {
  return (
    <ul className="space-y-1.5 text-sm leading-relaxed text-muted-foreground">
      {items.map((item) => (
        <li key={item} className="flex gap-2">
          <span aria-hidden className="mt-[7px] h-1.5 w-1.5 flex-none rounded-full bg-gold/70" />
          <span>{item}</span>
        </li>
      ))}
    </ul>
  );
}

function PracticeButtons({ response }: { response: TraumaResponse }) {
  const membership = useMembership();
  const hasAccess = membership.isMember || membership.isAdmin;

  return (
    <div className="grid gap-2 sm:grid-cols-2">
      {response.practices.map((id) => {
        const practice = PRACTICE_LIBRARY[id];
        const linkProps = practiceLinkProps(practice, hasAccess);
        return (
          <Link
            key={id}
            {...(linkProps as { to: string })}
            className="flex h-full min-h-[64px] flex-col justify-center rounded-2xl border border-gold/30 bg-card p-3 text-start transition hover:-translate-y-0.5 hover:shadow-md"
          >

            <span className="flex items-center gap-1.5 text-sm font-semibold text-foreground">
              {hasAccess ? null : (
                <Lock aria-hidden className="h-3.5 w-3.5 text-[var(--gold-deep)]" />
              )}
              {practice.title}
            </span>
            <span className="mt-1 text-xs leading-relaxed text-muted-foreground">
              {practice.blurb}
            </span>
            <span className="mt-1.5 inline-flex items-center gap-1 text-xs font-semibold text-[var(--gold-deep)]">
              {hasAccess ? "Open this practice" : "Members only — see membership"}
              <ArrowRight aria-hidden className="h-3.5 w-3.5" />
            </span>
          </Link>
        );
      })}
    </div>
  );
}

function LockedResponse({ response }: { response: TraumaResponse }) {
  return (
    <div className="mt-4 rounded-2xl border-2 border-gold/30 bg-card/90 px-5 py-6 text-center">
      <span
        className="mx-auto inline-flex h-11 w-11 items-center justify-center rounded-full"
        style={{ background: tint(response.color, 40), color: ink(response.color) }}
      >
        <Lock aria-hidden className="h-5 w-5" />
      </span>
      <p className="mt-3 font-display text-xl font-semibold text-foreground">
        Continue with Recovery Trauma™
      </p>
      <p className="mx-auto mt-2 max-w-md text-sm leading-relaxed text-foreground/80">
        Understand what this response may look like, what can trigger it, how it can affect your
        body and relationships, and explore practical tools that may help you regulate.
      </p>
      <div className="mt-4 flex flex-col items-center gap-2.5 sm:flex-row sm:justify-center">
        <Link
          to="/membership"
          className="inline-flex min-h-[48px] w-full max-w-xs items-center justify-center gap-2 rounded-full bg-[var(--gold-deep)] px-6 text-sm font-semibold text-primary-foreground sm:w-auto"
        >
          Join Recovery Trauma™ <ArrowRight aria-hidden className="h-4 w-4" />
        </Link>
        <Link
          to="/login"
          className="inline-flex min-h-[48px] w-full max-w-xs items-center justify-center rounded-full border border-gold/40 bg-card px-5 text-sm font-semibold text-foreground sm:w-auto"
        >
          Log In
        </Link>
      </div>
    </div>
  );
}


function ResponseDetail({
  response,
  onClose,
  locked = false,
}: {
  response: TraumaResponse;
  onClose: () => void;
  locked?: boolean;
}) {
  return (
    <div
      className="rounded-3xl border p-4 sm:p-5"
      style={{ borderColor: tint(response.color, 70), backgroundColor: tint(response.color, 12) }}
    >
      <header className="mb-3 flex items-start justify-between gap-3">
        <div>
          <h3 className="font-display text-lg" style={{ color: ink(response.color) }}>
            {response.title}
          </h3>
          <p className="mt-0.5 text-sm italic text-muted-foreground">“{response.voice}”</p>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label={`Close ${response.label} details`}
          className="inline-flex h-[44px] w-[44px] flex-none items-center justify-center rounded-full border border-gold/30 bg-card/80 text-foreground/70 shadow-sm transition hover:bg-gold/15 hover:text-foreground"
        >
          <X aria-hidden className="h-4 w-4" />
        </button>
      </header>

      {locked ? (
        <>
          <p className="text-sm leading-relaxed text-foreground/85">
            {response.whatIsHappening}
          </p>

          <div className="mt-4">
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-[var(--gold-deep)]">
              Common signs
            </p>
            <SignChips response={response} previewCount={Math.ceil(response.signs.length / 2)} />
          </div>

          <div className="mt-3">
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-[var(--gold-deep)]">
              You might notice this in your body
            </p>
            <Bullets items={response.body.slice(0, Math.max(2, Math.ceil(response.body.length / 2)))} />
          </div>

          <LockedResponse response={response} />
        </>
      ) : (
        <>
      <p className="text-sm leading-relaxed text-foreground/85">{response.whatIsHappening}</p>

      <div className="mt-4">
        <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-[var(--gold-deep)]">
          Common signs — swipe to read
        </p>
        <SignChips response={response} />
      </div>

      <Accordion type="single" collapsible className="mt-3">
        <AccordionItem value="body">
          <AccordionTrigger className="text-sm">You might notice this in your body</AccordionTrigger>
          <AccordionContent>
            <Bullets items={response.body} />
          </AccordionContent>
        </AccordionItem>
        <AccordionItem value="thoughts">
          <AccordionTrigger className="text-sm">
            You might notice this in your thoughts
          </AccordionTrigger>
          <AccordionContent>
            <Bullets items={response.thoughts} />
          </AccordionContent>
        </AccordionItem>
        <AccordionItem value="behaviour">
          <AccordionTrigger className="text-sm">
            You might notice this in your behaviour
          </AccordionTrigger>
          <AccordionContent>
            <Bullets items={response.behaviour} />
          </AccordionContent>
        </AccordionItem>
        <AccordionItem value="triggers">
          <AccordionTrigger className="text-sm">What might set this off?</AccordionTrigger>
          <AccordionContent>
            <Bullets items={response.triggers} />
          </AccordionContent>
        </AccordionItem>
        <AccordionItem value="now">
          <AccordionTrigger className="text-sm">What can I do right now?</AccordionTrigger>
          <AccordionContent>
            <Bullets items={response.rightNow} />
          </AccordionContent>
        </AccordionItem>
      </Accordion>

      <div className="mt-4">
        <p className="mb-2 text-sm font-semibold text-foreground">
          Practices that may help with {response.label.toLowerCase()}
        </p>
        <PracticeButtons response={response} />
      </div>
        </>
      )}
    </div>
  );
}

export function TraumaResponseWheel({
  initialKey = null,
}: {
  compact?: boolean;
  /** Kept for call-site compatibility; the layout is now always the clean wheel-first one. */
  slim?: boolean;
  /** Pre-select (highlight + open) a response, e.g. from the check-in result. */
  initialKey?: string | null;
}) {
  const [selected, setSelected] = useState<TraumaResponse | null>(() =>
    initialKey ? (TRAUMA_RESPONSES.find((r) => r.key === initialKey) ?? null) : null,
  );
  const [showMore, setShowMore] = useState(false);
  const detailRef = useRef<HTMLDivElement | null>(null);
  const wheelRef = useRef<HTMLDivElement | null>(null);
  const wheelMembership = useMembership();
  const wheelHasAccess = wheelMembership.isMember || wheelMembership.isAdmin;

  const choose = (r: TraumaResponse) => {
    setSelected((current) => (current?.key === r.key ? null : r));
    window.setTimeout(() => {
      const el = detailRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      // Only scroll when the detail heading sits below the fold, and scroll
      // just far enough to show it — the start of the page (heading + wheel)
      // stays in view so the reader never has to scroll back up.
      if (rect.top > window.innerHeight - 120) {
        const target =
          window.scrollY + rect.top - Math.round(window.innerHeight * 0.45);
        window.scrollTo({ top: Math.max(0, target), behavior: "smooth" });
      }
    }, 80);
  };

  const closeDetail = () => {
    setSelected(null);
    window.setTimeout(() => {
      wheelRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 80);
  };

  return (
    <section id="trauma-response-wheel" className="scroll-mt-24">
      <div className="mx-auto max-w-xl">
        {/* Clean page-style header — no boxed card around the intro */}
        <header>
          <h2 className="font-display text-2xl leading-tight text-foreground sm:text-3xl">
            Trauma Response Wheel
          </h2>
          <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
            Your nervous system reacts before you have time to think. Tap a colour to
            explore what may be running — and what can help.
          </p>
          <p className="mt-1.5 text-xs leading-relaxed text-muted-foreground/85">
            Self-reflection — not a diagnosis.
          </p>
          <button
            type="button"
            onClick={() => setShowMore((v) => !v)}
            aria-expanded={showMore}
            className="mt-3 inline-flex min-h-9 items-center gap-1.5 rounded-full border border-gold/45 bg-transparent px-4 text-xs font-semibold text-foreground transition hover:border-gold hover:bg-gold/10"
          >
            How it works
            <ChevronDown
              className={`h-3.5 w-3.5 transition-transform ${showMore ? "rotate-180" : ""}`}
              aria-hidden
            />
          </button>
          {showMore ? (
            <div className="mt-3 animate-fade-in space-y-3 rounded-2xl border border-gold/25 bg-card/60 p-4 text-[13px] leading-relaxed text-muted-foreground">
              <p>{TRAUMA_RESPONSE_NOT_FIXED}</p>
              <p>
                Choose a response on the wheel and we'll walk it through with you: what's
                happening, what you might notice in your body, thoughts and behaviour, what tends
                to set it off, and what may help right now.
              </p>
              <p>{TRAUMA_RESPONSE_DISCLAIMER}</p>
            </div>
          ) : null}
        </header>

        {/* The wheel is the hero — large and high on the page */}
        <div ref={wheelRef} className="mx-auto mt-4 w-full max-w-[300px] sm:max-w-[340px]">
          <Wheel selectedKey={selected?.key ?? null} onSelect={choose} />
        </div>

        {/* Information card directly underneath the wheel */}
        <div ref={detailRef} className={selected ? "mt-4 scroll-mt-24" : undefined}>
          {selected ? (
            <ResponseDetail response={selected} onClose={closeDetail} locked={!wheelHasAccess} />
          ) : null}
        </div>

        {/* Check-in card — clean single row */}
        <Link
          to="/trauma-response-check-in"
          className="mt-4 flex min-h-[64px] items-center gap-3 rounded-2xl border border-gold/35 bg-card/70 px-4 py-3 transition hover:border-gold hover:bg-gold/10"
        >
          <Sparkles aria-hidden className="h-5 w-5 flex-none text-[var(--gold-deep)]" />
          <span className="min-w-0 flex-1">
            <span className="block text-sm font-semibold text-foreground">
              Not sure which response you're in?
            </span>
            <span className="mt-0.5 block text-xs leading-relaxed text-muted-foreground">
              A few questions can help you find it.
              {wheelHasAccess ? "" : " Available to Recovery Trauma™ Members."}
            </span>
          </span>
          <ChevronRight aria-hidden className="h-4 w-4 flex-none text-muted-foreground" />
        </Link>

        {/* Crisis support — accessible but quiet */}
        <Link
          to="/crisis"
          className="mt-2.5 flex min-h-[52px] items-center gap-3 rounded-2xl border border-border/60 bg-transparent px-4 py-2.5 transition hover:border-gold/50 hover:bg-gold/5"
        >
          <LifeBuoy aria-hidden className="h-4.5 w-4.5 flex-none text-muted-foreground" />
          <span className="min-w-0 flex-1 text-sm font-medium text-foreground/85">
            I need support right now
          </span>
          <ChevronRight aria-hidden className="h-4 w-4 flex-none text-muted-foreground" />
        </Link>
      </div>
    </section>
  );
}
